import { useEffect, useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { CopyTextButton } from '@eluve/blocks';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Icon,
  Label,
  Stack,
  Text,
} from '@eluve/components';
import { SummaryGenerationInfoCard } from '@eluve/frontend-admin';
import { latestHumanSOAPNoteWithPromptTemplateFragment } from '@eluve/frontend-appointment-hooks';

interface LatestAppointmentTemplateProps {
  appointmentId: string;
  handleModelInfoUpdate?: (info: string) => void;
}

const LatestAppointmentTemplateContent: React.FC<
  LatestAppointmentTemplateProps
> = ({ appointmentId, handleModelInfoUpdate }) => {
  const appointment = useCompleteFragment({
    fragment: latestHumanSOAPNoteWithPromptTemplateFragment,
    key: {
      id: appointmentId,
    },
    strict: false,
  });

  const templateBundle = appointment?.template_bundle;

  const output = appointment?.humanOutputs[0]?.output?.llm_output;

  const promptTemplate = output?.metadata?.prompt_template;
  const promptVariant = output?.metadata?.prompt_variant;
  const outputTemplate = output?.metadata?.output_template;
  const outputVariant = output?.metadata?.output_variant;

  useEffect(() => {
    if (!promptTemplate || !output) return;
    const { args, id, modelType } = output.modelPromptTemplate.model_args;

    handleModelInfoUpdate?.(
      JSON.stringify(
        {
          modelArgs: {
            id,
            modelType,
            ...args,
          },
          llmOutputTemplate: {
            id: outputTemplate?.id,
            name: outputTemplate?.name,
          },
          promptTemplate: {
            id: promptTemplate?.id,
            name: promptTemplate?.name,
          },
        },
        null,
        2,
      ),
    );
  }, [output, handleModelInfoUpdate, promptTemplate, outputTemplate]);

  if (!output) {
    return (
      <CollapsibleContent>
        <Stack
          className="rounded-lg border border-border-warning-subtle bg-bg-warning-subtle p-3 text-sm text-text-warning-subtle"
          direction="horizontal"
          gap={3}
          align="stretch"
        >
          <Icon name="TriangleAlert" size="lg" />
          No summary generated for this appointment.
        </Stack>
      </CollapsibleContent>
    );
  }

  return (
    <CollapsibleContent>
      <SummaryGenerationInfoCard
        templateBundle={templateBundle}
        promptTemplate={promptTemplate}
        promptVariantNumber={promptVariant?.variantNumber}
        outputTemplate={outputTemplate}
        outputVariantNumber={outputVariant?.variantNumber}
        modelArgs={output.modelPromptTemplate.model_args}
        startedAt={output.startedAt}
        completedAt={output.completedAt}
      />
    </CollapsibleContent>
  );
};

export const LatestAppointmentTemplate: React.FC<
  LatestAppointmentTemplateProps
> = ({ appointmentId }) => {
  const [modalInfo, setModalInfo] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  return (
    <Collapsible open={opened} onOpenChange={setOpened}>
      <div className="space-y-2">
        <Stack direction="horizontal" fullWidth={false} gap={2}>
          <Label className="px-1">Latest Summary Info</Label>
          <CopyTextButton
            disabled={!modalInfo}
            copyText={modalInfo ?? ''}
            hideContentInTooltip
          />
          <CollapsibleTrigger>
            <Stack direction="horizontal" fullWidth={false} gap={2}>
              <Icon
                name="ChevronDown"
                size="lg"
                className='transition-transform duration-300 ease-in-out data-[state="open"]:-rotate-180'
                data-state={opened ? 'open' : 'closed'}
              />
              <Text inline>{!opened ? 'Show' : 'Hide'}</Text>
            </Stack>
          </CollapsibleTrigger>
        </Stack>
        <LatestAppointmentTemplateContent
          appointmentId={appointmentId}
          handleModelInfoUpdate={setModalInfo}
        />
      </div>
    </Collapsible>
  );
};
